import type React from "react";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import ColorPicker from "senf-atomic-design-system/src/components/core/inputs/ColorPicker";
import ListItem from "senf-atomic-design-system/src/components/core/list/ListItem";
import { cn } from "senf-atomic-design-system/src/util/classNameConstructor";
import StyleConditionsFileHandler from "./StyleConditionsFileHandler";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface BimStyleConditionsProps {
  conditions: StyleCondition[];
  onStyleConditionsChange: (conditions: StyleCondition[]) => void;
  highlightedCondition?: { property: string; value: string } | null;
}

export const BimStyleConditions: React.FC<BimStyleConditionsProps> = ({
  conditions,
  onStyleConditionsChange,
  highlightedCondition,
}) => {
  const handleRemoveCondition = (index: number) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    onStyleConditionsChange(updatedConditions);
  };

  const handleColorChange = (index: number, color: string, opacity: number) => {
    const updatedConditions = conditions.map((condition, i) => {
      if (i === index) {
        return { ...condition, color, opacity };
      }
      return condition;
    });
    onStyleConditionsChange(updatedConditions);
  };

  const handleToggleVisibility = (index: number) => {
    const condition = conditions[index];
    const newOpacity = condition.opacity === 0 ? 1.0 : 0;
    handleColorChange(index, condition.color, newOpacity);
  };

  // if (conditions.length === 0) {
  //   return (
  //     <div className="">
  //       {/* <div className="p-4 text-center text-text-subdued">
  //         <p>No style conditions yet.</p>
  //         <p className="text-sm">Upload BIM models to see style options.</p>
  //       </div> */}

  //       <StyleConditionsFileHandler
  //         conditions={conditions}
  //         onConditionsChange={onStyleConditionsChange}
  //         variant="empty"
  //       />
  //     </div>
  //   );
  // }

  return (
    <div className="">
      <div className="space-y-2 mt-4">
        {conditions
          .slice()
          .reverse()
          .map((condition, reversedIndex) => {
            const originalIndex = conditions.length - 1 - reversedIndex;
            return (
              <div
                key={`${condition.property}-${condition.value}-${originalIndex}`}
                className="flex items-center"
              >
                <Button
                  variant="shapeless"
                  size="sm"
                  square
                  leadingIcon={
                    condition.opacity === 0 ? "eye-strikethrough" : "eye"
                  }
                  onClick={() => handleToggleVisibility(originalIndex)}
                  classNameWrapper="pr-3"
                />
                <div
                  className={cn(
                    "relative p-3 border rounded-xl w-full",
                    // Highlight the condition if it matches the highlighted condition
                    highlightedCondition &&
                      condition.property === highlightedCondition.property &&
                      condition.value === highlightedCondition.value &&
                      "ring-2 ring-primary-default bg-primary-50",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1 items-center">
                      <ColorPicker
                        initialColor={condition.color}
                        initialOpacity={condition.opacity}
                        handleSave={async (color, opacity) => {
                          await new Promise((resolve) =>
                            setTimeout(resolve, 0),
                          );
                          handleColorChange(originalIndex, color, opacity);
                        }}
                      />

                      <div className="space-y-1 pl-3">
                        <div className="line-clamp-1 break-all text-ellipsis">
                          <p className="text-xs text-text-default mt-2 text-wrap px-1 py-0.5 font-mono bg-neutral-100 text-neutral-500 inline rounded-sm">
                            {condition.property || "No property"}
                          </p>
                        </div>
                        <div className="line-clamp-1 break-all text-ellipsis">
                          <p className="body-sm-bold text-text-default text-wrap">
                            {condition.value || "No value"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="shapeless"
                      size="sm"
                      square
                      leadingIcon="trash"
                      critical
                      onClick={() => handleRemoveCondition(originalIndex)}
                      className=""
                    />
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* <StyleConditionsFileHandler
        conditions={conditions}
        onConditionsChange={onStyleConditionsChange}
        disabled={conditions.length === 0}
      /> */}
    </div>
  );
};

export default BimStyleConditions;
